import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, matchPath, NavLink, useParams } from "react-router";
import { useClientDropdown } from "../../api/useClientDropdown";
import { useClientRouteOverrides } from "../../data/global/useProductConfiguration";
import { usePayerLandscapeDispatch } from "../../data/payerLandscape/hooks";
import { useClientRoutesAndLabels } from "../../ui/routes";

interface Props {
  closeNav: () => void;
}

interface ClientNavItemProps {
  client: string;
  selectedClient?: string;
  currentPathname: string;
  onClientSelected: (client: string) => void;
}

const ClientNavItem: React.FC<ClientNavItemProps> = ({ client, selectedClient, currentPathname, onClientSelected }) => {
  const [t, i18n] = useTranslation();
  const routeOverrides = useClientRouteOverrides(client); // Call hook at top level

  // Get the hcps path segment for this client
  const hcpsSegment =
    typeof routeOverrides?.client === "object" &&
    typeof routeOverrides.client.dpp === "object" &&
    typeof routeOverrides.client.dpp.hcps === "object" &&
    "path" in routeOverrides.client.dpp.hcps
      ? (routeOverrides.client.dpp.hcps as { path: string }).path
      : "hcps";

  // Build the correct path for this client
  const getNewPath = (): string => {
    // Check if we're on an HCP detail page
    const hcpDetailMatch =
      matchPath({ path: "/:client/dpp/hcps/:id", end: false }, currentPathname) ||
      matchPath({ path: "/:client/dpp/accounts/:id", end: false }, currentPathname);

    // Check if we're on HCP landscape
    const hcpLandscapeMatch =
      matchPath({ path: "/:client/dpp/hcps", end: true }, currentPathname) ||
      matchPath({ path: "/:client/dpp/accounts", end: true }, currentPathname);

    if (hcpDetailMatch?.params.id) {
      // Go to landscape page instead of detail page when switching clients
      return `/${client}/dpp/${hcpsSegment}`;
    }

    if (hcpLandscapeMatch) {
      return `/${client}/dpp/${hcpsSegment}`;
    }

    // For all other routes, just replace the client name
    return currentPathname.replace(selectedClient ?? "", client);
  };

  const newPath = getNewPath();

  return (
    <li className="body-2">
      <NavLink
        className={({ isActive }) => `px-5 flex items-center${isActive ? " active-nav" : ""}`}
        onClick={() => onClientSelected(client)}
        to={newPath}
      >
        <>
          <div style={{ minWidth: "26px", minHeight: "26px" }}>
            <img className="max-w-[26px]" alt={client} src={`/assets/icons/${client}.png`} />
          </div>
          <span className="ml-3">
            {i18n.exists(`defaultLayout.appNames.${client}`)
              ? t(`defaultLayout.appNames.${client}`)
              : t("defaultLayout.appName", { product: client?.split("_")?.join(" ") })}
          </span>
        </>
      </NavLink>
    </li>
  );
};

const ClientNavigation: React.FC<Props> = ({ closeNav }) => {
  const [t, i18n] = useTranslation();
  const { client: selectedClient } = useParams<{ client: string }>();
  const { updateDetailFilters } = usePayerLandscapeDispatch();
  const { data: clients } = useClientDropdown();
  const location = window.location;
  const match = matchPath({ path: "/:client/*", end: false }, location.pathname);
  const routes = useClientRoutesAndLabels(selectedClient);

  const onClientSelected = () => {
    updateDetailFilters({ product: "" });
    closeNav();
  };

  if (!clients || clients.length === 1) {
    return (
      <Link to={routes.path} className="hover:bg-light-blue pt-4 pb-3 px-3 mt-px focus:ring">
        {i18n.exists(`defaultLayout.appNames.${selectedClient}`)
          ? t(`defaultLayout.appNames.${selectedClient}`)
          : t("defaultLayout.appName", { product: selectedClient?.split("_")?.join(" ") })}
      </Link>
    );
  }

  return (
    <div className="lg:flex flex-grow lg:space-x-3">
      <div className="nav-container relative">
        <NavLink
          onClick={closeNav}
          to={routes.path}
          end
          className={({ isActive }) =>
            `nav flex justify-between hover:bg-light-blue focus:ring${isActive ? " active-nav" : ""}`
          }
        >
          <>
            {i18n.exists(`defaultLayout.appNames.${selectedClient}`)
              ? t(`defaultLayout.appNames.${selectedClient}`)
              : t("defaultLayout.appName", { product: selectedClient?.split("_")?.join(" ") })}
            <span className="hidden ml-2 lg:inline">
              <KeyboardArrowDownIcon />
            </span>
          </>
        </NavLink>
        <ul className="sub-nav w-full">
          {match &&
            clients?.map((client) => (
              <ClientNavItem
                key={client}
                client={client}
                selectedClient={selectedClient}
                currentPathname={match.pathname}
                onClientSelected={onClientSelected}
              />
            ))}
        </ul>
      </div>
    </div>
  );
};

export default ClientNavigation;
